import { arrayIdentity } from "./util";

const BR = /\<br\s*\/?\>/gi;

export async function extract(source, constraints = [], config = {}) {
  let extracted = [],
    datatype,
    rows = [],
    columns = [],
    text_count = 0,
    filter_count = 0,
    id_mappings = {};

  // 1. data-related
  // get dataset
  if (source.datatype !== undefined) {
    datatype = source.datatype;
  } else {
    datatype = {};
    if (source.data !== undefined) {
      let datastats = getDatasetInfo(source.data);
      for (const info_row of datastats) {
        datatype[info_row.field] = {
          type: info_row.type,
          entropy: info_row.entropy,
          cardinality: info_row.cardinality,
          values: info_row.values,
          min: info_row.min,
          max: info_row.max,
        }
      }
    }
  }
  // # data type def
  if (datatype) {
    for (const name in datatype) {
      extracted.push(["datafield", `"${name}"`, datatype[name].type, datatype[name].cardinality])
    }
  }
  // # data rows
  if (source.data !== undefined) {
    let n_data_rows = getDataRows(source.data);
    if (n_data_rows) {
      extracted.push(["data_rows", n_data_rows]);
    }
  }

  // range information
  for (const field in datatype) {
    let min = datatype[field]?.min, max = datatype[field]?.max;
    if (isValue(min) && isValue(max) && sign(min) != sign(max)) {
      extracted.push(["range_has_zero", `"${field}"`]);
    }
  }

  if (config.data_filter !== undefined) {
    extracted.push(["detect", "filter_suggested"]);
  }

  if (source.globalLock) {
    if (source.globalLock.data) {
      extracted.push(["locked", "data"]);
    }
    if (source.globalLock.transform) {
      extracted.push(["locked", "transform"]);
    }
  }

  // 2. layout-related
  if (isValue(config.artboard_width)) extracted.push(["intended_width", config.artboard_width]);
  if (isValue(config.artboard_height)) extracted.push(["intended_height", config.artboard_height]);

  if (isValue(source.layout?.width)) extracted.push(["original_width", source.layout.width]);
  if (isValue(source.layout?.height)) extracted.push(["original_height", source.layout.height]);

  if (isValue(config.device_screen_size, "Array")) {
    if (isValue(config.device_screen_size[0], "Number")) extracted.push(["intended_device_width", config.device_screen_size[0]]);
    if (isValue(config.device_screen_size[1], "Number")) extracted.push(["intended_device_height", config.device_screen_size[1]]);
  }

  if (isValue(source.layout?.padding)) extracted.push(["has_padding", "view"]);

  if (isValue(config.artboard_width) && isValue(source.layout?.width))
    extracted.push(["change_width", Math.round(config.artboard_width - source.layout?.width)]);

  if (isValue(config.artboard_height) && isValue(source.layout?.height))
    extracted.push(["change_height", Math.round(config.artboard_height - source.layout?.height)]);

  if (isValue(config.artboard_width) && isValue(source.layout?.width) && isValue(config.artboard_height) && isValue(source.layout?.height)) {
    let size1 = source.layout?.width * source.layout?.height;
    let size2 = config.artboard_width * config.artboard_height;
    let size_diff = size2 - size1;
    if (Math.abs(size_diff) < size1 * 0.3 || Math.abs(size_diff) < size2 * 0.3) extracted.push(["change_size", "slight"]);
    extracted.push(["change_size", Math.round(size_diff)]);
    let ar1 = source.layout?.width / source.layout?.height;
    let ar2 = config.artboard_width / config.artboard_height;
    extracted.push(["aspect_ratio", getAspectRaitoType(ar1)]);
    extracted.push(["intended_aspect_ratio", getAspectRaitoType(ar2)]);
    if (Math.abs(ar1 - ar2) < 0.1)
      extracted.push(["change_aspect_ratio", "slight"]);
    if (Math.abs(ar1 / (1 / ar2)) < 1.5 && (gtAbsRatio(ar1, 1.4) || gtAbsRatio(ar2, 1.4)))
      extracted.push(["change_aspect_ratio", "extremete"])
  }

  if (isValue(config.original_screen)) extracted.push(["device_type", config.original_screen])
  if (isValue(config.original_orient)) extracted.push(["screen_orient", config.original_orient])
  if (isValue(config.intended_screen)) extracted.push(["intended_device_type", config.intended_screen])
  if (isValue(config.intended_orient)) extracted.push(["intended_screen_orient", config.intended_orient])

  let rcDimensions = [];

  if (source.layout !== undefined) {
    let layout = source.layout;
    // layout composition
    if (layout.composition !== undefined) {
      if (layout.composition === "projection") {
        extracted.push(["composition", "projection"]);
        if (layout.projection.type) {
          extracted.push(["projection_type", layout.projection.type]);
        } else {
          extracted.push(["projection_type", "mercator"]);
        }
      }
      else if (layout.composition === "repeated") {
        extracted.push(["composition", "repeated"]);
        if (layout.nColumns !== undefined) extracted.push(["facet_columns", layout.nColumns]);
        if (layout.split !== undefined) {
          if (layout.split.field !== undefined) {
            if (layout.split.values !== undefined) {
              extracted.push(["split", "by_field", `"${layout.split.field}"`, `${layout.split.values.length}`]);
              extracted.push(["field", `"${layout.split.field}"`, "split", `${layout.split.values.length}`]);
            } else {
              extracted.push(["split", "by_field", `"${layout.split.field}"`, `${datatype[layout.split.field].cardinality}`]);
              extracted.push(["field", `"${layout.split.field}"`, "split", `${datatype[layout.split.field].cardinality}`]);
            }
            rcDimensions.push(layout.split.field);
          } else if (layout.split.conditions !== undefined) {
            extracted.push(["split", "by_filter"]);
          }
        }
      } else extracted.push(["composition", "single"]);
    }
    if (layout.lock) {
      if (layout.lock.composition) extracted.push(["locked", "composition"]);
      if (layout.lock.transpose) extracted.push(["locked", "transpose"]);
      if (layout.lock.nColumns) extracted.push(["locked", "nColumns"]);
    }
    // row
    if (layout.row !== undefined && layout.row.constructor.name === "Array") {
      let row_count = 0;
      for (const ri in layout.row) {
        let row_id = "r" + row_count.toString();
        let row_elem = layout.row[ri];
        let row_type, row_name, row_card;
        if (row_elem.constructor.name === "Array") {
          row_elem = row_elem[0];
        }
        if (row_elem.constructor.name === "String") {
          row_name = row_elem;
          row_type = datatype[row_elem]?.type;
          row_card = datatype[row_elem]?.cardinality;
        } else if (row_elem.constructor.name === "Object") {
          row_name = row_elem.field;
          row_type = row_elem.type || datatype[row_name].type;
          row_card = datatype[row_name].cardinality;
          if (row_elem.aggregate !== undefined) {
            extracted.push(["aggregate", "row", row_id, row_elem["aggregate"]]);
          }
          if (row_elem.bin !== undefined) {
            let binCount = row_elem.bin.maxbins || "und";
            extracted.push(["bin", "row", row_id, binCount]);
          }
          if (row_elem.zero !== undefined && row_elem.zero) {
            extracted.push(["zero", "row", row_id]);
          } else if (row_elem.zero !== undefined && !row_elem.zero) {
            extracted.push(["not zero", "row", row_id]);
          } else if (row_elem.zero === undefined && row_type === "quantitative") {
            if (row_elem.domain !== undefined && (row_elem["domain"][0] != 0 || row_elem["domain"][row_elem["domain"].length - 1] != 0)) {
              extracted.push(["not zero", "row", row_id]);
            } else {
              extracted.push(["zero", "row", row_id]);
            }
          }
          if (row_elem.stack) {
            extracted.push(["stack", "row", row_id, row_elem.stack]);
          }
        }
        if (row_type === "nominal" || row_type === "ordinal") {
          rcDimensions.push(row_name);
        }
        if (row_elem.lock) {
          extracted.push(["locked", row_id]);
        }
        if (layout.composition === "projection") { row_card = 200; row_type = "quantitative"; }
        rows.push(row_name);
        extracted.push(["row_id", row_id]);
        extracted.push(["row", row_id, row_type, row_card]);
        extracted.push(["field", `"${row_name}"`, "row", row_id]);
        id_mappings[row_id] = { "layout.row": ri };
        row_count += 1;
      }
      if (row_count > 0) {
        extracted.push(["last_row", `r${row_count - 1}`]);
        extracted.push(["first_row", "r0"]);
      }
    }
    // column
    if (layout.column !== undefined && layout.column.constructor.name === "Array") {
      let column_count = 0;
      for (const ci in layout.column) {
        let column_id = "c" + column_count.toString();
        let column_elem = layout.column[ci];
        let column_type, column_name, column_card;
        if (column_elem.constructor.name === "Array") {
          column_elem = column_elem[0];
        }
        if (column_elem.constructor.name === "String") {
          column_name = column_elem;
          column_type = datatype[column_elem]?.type;
          column_card = datatype[column_elem]?.cardinality;
        } else if (column_elem.constructor.name === "Object") {
          column_name = column_elem.field;
          column_type = column_elem.type || datatype[column_name].type;
          column_card = datatype[column_name].cardinality;
          if (column_elem.aggregate !== undefined) {
            extracted.push(["aggregate", "column", column_id, column_elem["aggregate"]]);
          }
          if (column_elem.bin !== undefined) {
            let binCount = column_elem.bin.maxbins || "und";
            extracted.push(["bin", "column", column_id, binCount]);
          }
          if (column_elem.zero !== undefined && column_elem.zero) {
            extracted.push(["zero", "column", column_id]);
          } else if (column_elem.zero !== undefined && !column_elem.zero) {
            extracted.push(["not zero", "column", column_id]);
          } else if (column_elem.zero === undefined && column_type === "quantitative") {
            if (column_elem.domain !== undefined && (column_elem["domain"][0] != 0 || column_elem["domain"][column_elem["domain"].length - 1] != 0)) {
              extracted.push(["not zero", "column", column_id]);
            } else {
              extracted.push(["zero", "column", column_id]);
            }
          }
          if (column_elem.stack) {
            extracted.push(["stack", "column", column_id, column_elem.stack]);
          }
        }
        if (column_type === "nominal" || column_type === "ordinal") {
          rcDimensions.push(column_name);
        }
        if (column_elem.lock) {
          extracted.push(["locked", column_id]);
        }
        if (layout.composition === "projection") { column_card = 200; column_type = "quantitative"; }
        columns.push(column_name);
        extracted.push(["column_id", column_id]);
        extracted.push(["column", column_id, column_type, column_card]);
        extracted.push(["field", `"${column_name}"`, "column", column_id]);
        id_mappings[column_id] = { "layout.column": ci };
        column_count += 1;
      }
      if (column_count > 0) {
        extracted.push(["last_column", `c${column_count - 1}`]);
        extracted.push(["first_column", "c0"]);
      }
    }
    // grid, domain
    if (layout.haxis) {
      if (layout.haxis.grid) extracted.push(["axis_grid", "haxis"])
      if (layout.haxis.domain) extracted.push(["axis_domain", "haxis"])
      if (layout.haxis.lock) {
        extracted.push(["locked", "haxis"]);
      }
    }
    if (layout.vaxis) {
      if (layout.vaxis.grid) extracted.push(["axis_grid", "vaxis"])
      if (layout.vaxis.domain) extracted.push(["axis_domain", "vaxis"])
      if (layout.vaxis.lock) {
        extracted.push(["locked", "vaxis"]);
      }
    }
  }

  // check [bird-something theroy]
  if (rcDimensions.length > 0 && datatype !== undefined) {
    let rcMeasures = Object.keys(datatype).filter(f => {
      return ["quantitative", "temporal"].includes(datatype[f].type) && !rcDimensions.includes(f)
    });
    let rcDimCount = {}
    source?.data?.forEach(row => {
      let dimKey = rcDimensions.map(dim => row[dim]).join("$")
      rcMeasures.forEach(mesKey => {
        if (rcDimCount[mesKey] === undefined) rcDimCount[mesKey] = {};
        if (rcDimCount[mesKey][dimKey] === undefined) rcDimCount[mesKey][dimKey] = 0;
        rcDimCount[mesKey][dimKey] += 1;
      })
    });
    Object.keys(rcDimCount).forEach(mesKey => {
      if (Object.values(rcDimCount[mesKey]).every(d => { return d <= 1; })) {
        extracted.push(["not_aggregatable", `"${mesKey}"`, "global"]);
      }
    })
  }
  //  3. data transformation
  if (isValue(source.transform)) {
    for (const ti in source.transform) {
      let transform = source.transform[ti];
      if (transform.filter !== undefined) {
        let filter_id = "f" + filter_count.toString();
        id_mappings[filter_id] = { layer: "global", transform: ti };
        extracted.push(["filter", "global", filter_id]);
        filter_count += 1;
      }
      if (transform.aggregate !== undefined) {
        let field = transform.field;
        if (rows.includes(field)) {
          let row_id = "r" + rows.indexOf(field).toString();
          extracted.push(["aggregate", "row", row_id]);
        }
        if (columns.includes(field)) {
          let col_id = "c" + columns.indexOf(field).toString();
          extracted.push(["aggregate", "column", col_id]);
        }
      }
      if (transform.bin !== undefined) {
        let field = transform.field, binCount = transform.maxbins || "und";
        if (rows.includes(field)) {
          let row_id = "r" + rows.indexOf(field).toString();
          extracted.push(["bin", "row", row_id, binCount]);
        }
        if (columns.includes(field)) {
          let col_id = "c" + columns.indexOf(field).toString();
          extracted.push(["bin", "column", col_id, binCount]);
        }
      }
    }
  }

  // 4. layers
  if (isValue(source.layer)) {
    let layer_count = 0;
    for (const li in source.layer) {
      let layer = source.layer[li];
      let layer_id = "l" + layer_count.toString();
      id_mappings[layer_id] = { layer: parseInt(li) };
      extracted.push(["layer_id", layer_id]);
      // mark
      if (isValue(layer.mark)) {
        let marktype;
        let encDimensions = [];
        for (const propKey in layer.mark) {
          if (propKey === "lock") {
            if (layer.mark[propKey]) {
              extracted.push(["locked", layer_id, "mark"]);
            }
          }
          else if (propKey === "type") {
            marktype = layer.mark.type;
            extracted.push(["mark", layer_id, marktype]);
          } else if (propKey === "endMark" && marktype === "line") {
            if (isValue(layer.mark.endMark.type)) extracted.push(["line_end_mark", layer_id, layer.mark.endMark.type]);
            else extracted.push(["line_end_mark", layer_id, "circle"]);
          } else if (propKey === "line" && marktype === "area") {
            if (layer.mark.line) extracted.push(["line_on_area", layer_id]);
          } else if (propKey === "point") {
            extracted.push(["pointed_mark", layer_id, marktype]);
            if (isValue(layer.mark.point.filled) && layer.mark.point.filled) extracted.push(["pointed_mark_filled", layer_id, marktype]);
          } else {
            if (layer.mark[propKey].constructor.name === "Object") {
              let enc_type;
              if (isValue(layer.mark[propKey].type)) enc_type = layer.mark[propKey].type;
              else if (isValue(layer.mark[propKey].field)) enc_type = datatype[layer.mark[propKey].field].type;
              else if (isValue(layer.mark[propKey].value)) enc_type = "static";

              if (isValue(enc_type)) extracted.push(["encoding", layer_id, propKey, enc_type])
              if (enc_type !== "static") {
                extracted.push(["field", `"${layer.mark[propKey].field}"`, layer_id, propKey])
                if (propKey === "size") {
                  if (isValue(layer.mark[propKey].scale?.range) && layer.mark[propKey].scale?.range.constructor.name === "Array")
                    extracted.push(["rescalable", layer_id, propKey]);
                }
                if (enc_type === "nominal" || enc_type === "ordinal") {
                  encDimensions.push(layer.mark[propKey].field);
                }
              } else if (enc_type === "static" && propKey === "size") {
                if (isValue(layer.mark[propKey].value) && layer.mark[propKey].value.constructor.name === "Number")
                  extracted.push(["rescalable", layer_id, propKey]);
              }
              if (isValue(layer.mark[propKey].aggregate)) {
                extracted.push(["aggregate", layer_id, propKey, layer.mark[propKey].aggregate])
              }
              if (isValue(layer.mark[propKey].bin)) {
                let binCount = layer.mark[propKey].bin.maxbins || "und";
                extracted.push(["bin", layer_id, propKey, binCount]);
              }
              if (layer.mark[propKey].lock) {
                extracted.push(["locked", layer_id, propKey]);
              }
            }
          }
        }

        let n_channels = Object.keys(layer.mark).filter((propKey) => layer.mark[propKey].field || layer.mark[propKey].aggregate).length;
        if (layer.mark.type !== "geoshape") n_channels += 1;
        extracted.push(["n_channels", layer_id, n_channels]);

        // check [bird-something theroy]
        if ((rcDimensions.length + encDimensions.length) > 0 && datatype !== undefined) {
          let layerDimensions = [];
          layerDimensions.push(...rcDimensions);
          layerDimensions.push(...encDimensions);
          let layerMeasures = Object.keys(datatype).filter(f => {
            return ["quantitative", "temporal"].includes(datatype[f].type) && !layerDimensions.includes(f)
          });
          let layerDimCount = {}
          source?.data?.forEach(row => {
            let dimKey = layerDimensions.map(dim => row[dim]).join("$")
            layerMeasures.forEach(mesKey => {
              if (layerDimCount[mesKey] === undefined) layerDimCount[mesKey] = {};
              if (layerDimCount[mesKey][dimKey] === undefined) layerDimCount[mesKey][dimKey] = 0;
              layerDimCount[mesKey][dimKey] += 1;
            })
          });
          Object.keys(layerDimCount).forEach(mesKey => {
            if (Object.values(layerDimCount[mesKey]).every(d => { return d <= 1; })) {
              extracted.push(["not_aggregatable", `"${mesKey}"`, layer_id]);
            }
          })
        }
      }

      // text
      if (isValue(layer.text)) {
        for (const ti in layer.text) {
          let text_item = layer.text[ti];
          let text_id = "t" + text_count.toString();
          id_mappings[text_id] = { layer: li, text: ti };
          extracted.push(["text_id", text_id]);
          let text_type = { "on-axis": "axis", "on-mark": "mark", "legend": "legend" }[text_item.type] || null;
          let text_field = text_item.field || null;
          if (isValue(text_type) && isValue(text_field))
            extracted.push(["text", layer_id, text_id, text_type, `"${text_field}"`]);
          let label_card;
          if (text_item.values) {
            label_card = text_item.values.length;
            extracted.push(["text_valued", text_id]);
          }
          if (label_card === undefined && text_type === "mark") {
            label_card = datatype[text_field].cardinality;
          }
          if (isValue(label_card)) {
            extracted.push(["text_cardinality", text_id, label_card]);
          }
          if (text_type === "axis" && isValue(text_item.values) && isValue(text_item.tick?.tickValues)) {
            if (!arrayIdentity(text_item.values, text_item.tick?.tickValues)) {
              extracted.push(["tick_cardinality", text_id, text_item.tick?.tickValues.length]);
            }
          }
          if (text_type === "axis" && isValue(text_field)) {
            if (rows.includes(text_field)) extracted.push(["axis_label", text_id, "vaxis", `"${text_field}"`]);
            if (columns.includes(text_field)) extracted.push(["axis_label", text_id, "haxis", `"${text_field}"`]);
            if (isValue(text_item.items?.[0]?.format)) extracted.push(["text_format", text_id, `"${text_item.items[0].format}"`]);
            if (text_item.values?.includes(0)) {
              extracted.push(["axis_label_has_zero", text_id]);
            }
            if (isValue(text_item.orient)) extracted.push(["axis_label_orient", text_id, text_item.orient.replace("-", "_")]);
          }
          if (isValue(text_field)) extracted.push(["field", `"${text_field}"`, layer_id, text_id]);

          if (text_item.visibility?.externalize) {
            extracted.push(["text_external", text_id]);
            if (text_item.visibility.externalize?.numbering) extracted.push(["text_numbering", text_id]);
          }
          if (text_item.serial) extracted.push(["text_serial", text_id]);
          if (text_item.tick) extracted.push(["text_tick", text_id]);
          if (isValue(text_item.items, "Array")) {
            extracted.push(["text_lines", text_id, text_item.items.length]);
            let check_br_wrapped = false;
            for (const elem of text_item.items) {
              if (isValue(elem.width)) extracted.push(["text_wrapped", text_id]);
              if (isValue(elem.rotate)) extracted.push(["text_rotated", text_id]);
              if (text_type === "mark") {
                if (isValue(elem.expression, "String")) {
                  let simple_pattern1 = /\((datum\.)[a-zA-Z0-9\_\-]+\)/gi;
                  let simple_pattern2 = /(datum\.)[a-zA-Z0-9\_\-]+/gi;
                  if (elem.expression.match(simple_pattern1) !== null && elem.expression.match(simple_pattern2) !== null)
                    extracted.push(["text_customed", text_id]);
                } else if (isValue(elem.custom) || isValue(elem.content)) extracted.push(["text_customed", text_id]);
              }
              if (isValue(elem.text, "String") && elem.text.match(BR)) check_br_wrapped = true;
              if (isValue(elem.expression, "String") && elem.expression.match(BR)) check_br_wrapped = true;
              if (isValue(elem.custom)) {
                Object.keys(elem.custom).forEach((c) => {
                  if (isValue(elem.custom[c], "String") && elem.custom[c].match(BR)) check_br_wrapped = true;
                });
              }
            }
            if (check_br_wrapped) {
              extracted.push(["text_br_wrapped", text_id]);
            }
          }
          if (text_type === "legend") {
            extracted.push(["legend", text_id, label_card]);
            if (isValue(text_item.orient)) extracted.push(["legend_orient", text_id, text_item.orient.replace("-", "_")]);
          }
          // if it is highlighted
          if (text_type === "mark" && text_field) {
            let card = datatype[text_field].cardinality;
            if (text_item.values?.length < card / 2) {
              extracted.push(["field_value_highlight", "by_mark_label", layer_id, text_id, `"${text_field}"`]);
            }
          }
          if (text_item.lock) {
            extracted.push(["locked", layer_id, text_id]);
          }
          if (text_item.positionLock) {
            extracted.push(["position_locked", layer_id, text_id]);
          }
          text_count += 1;
        }
      }

      // layer tooltip
      if (isValue(layer.tooltip)) {
        extracted.push(["tooltip", layer_id]);
        if (layer.tooltip.visibility === "fixed") extracted.push(["tooltip_fixed", layer_id]);
        if (layer.tooltip.lock) {
          extracted.push(["locked", layer_id, "tooltip"]);
        }
        if (layer.tooltip.positionLock) {
          extracted.push(["position_locked", layer_id, "tooltip"]);
        }
      }

      // layer-wise data transform
      if (isValue(layer.transform)) {
        for (const ti in layer.transform) {
          let transform = layer.transform[ti];
          if (transform.filter !== undefined) {
            let filter_id = "f" + filter_count.toString();
            id_mappings[filter_id] = { layer: li, transform: ti };
            extracted.push(["filter", layer_id, filter_id]);
            filter_count += 1;
          }
        }
      }

      layer_count += 1;
    }
  }

  // 6. annotation
  if (isValue(source.annotation?.items)) {
    let annotation = source.annotation.items;
    for (const ni in annotation) {
      let annotation_item = annotation[ni];
      let anno_id = "a" + ni.toString();
      id_mappings[anno_id] = { annotation: ni };
      if (isValue(annotation_item.name)) id_mappings[anno_id].name = annotation_item.name;
      extracted.push(["annotation", anno_id]);
      if (isValue(annotation_item.text)) {
        let l = 0;
        if (isValue(annotation_item.text, "Array")) {
          for (const text_item of annotation_item.text) l += text_item.text?.length || text_item.length
        } else if (isValue(annotation_item.text, "String")) l = annotation_item.text.length;
        extracted.push(["length", anno_id, l]);
      }
      if (annotation_item.anchor?.dataItem !== undefined) {
        extracted.push(["data_annotation", anno_id]);
      }
      if (annotation_item.axisAssist) {
        extracted.push(["axis_assist", anno_id]);
        if (isValue(annotation_item.field)) extracted.push(["axis_assist", anno_id, annotation_item.field]);
      }
      if (annotation_item.visibility?.externalize) {
        extracted.push(["text_external", anno_id]);
        if (annotation_item.visibility?.externalize.numbering) extracted.push(["text_numbering", anno_id])
      }
      if (annotation_item.lock) {
        extracted.push(["locked", anno_id]);
      }
      if (annotation_item.positionLock) {
        extracted.push(["position_locked", anno_id]);
      }
      if (isValue(annotation_item.rotate)) {
        extracted.push(["text_rotated", anno_id]);
      }
    }
  }

  // 7.emphasis
  if (isValue(source.emphasis?.items)) {
    let emphasis = source.emphasis.items;
    for (const ni in emphasis) {
      let emphasis_item = emphasis[ni];
      let emph_id = "e" + ni.toString();
      id_mappings[emph_id] = { emphasis: ni };
      if (isValue(emphasis_item.name)) id_mappings[emph_id].name = emphasis_item.name;
      extracted.push(["emphasis", emph_id]);
      if (emphasis_item.lock) {
        extracted.push(["locked", emph_id]);
      }
      if (emphasis_item.positionLock) {
        extracted.push(["position_locked", emph_id]);
      }
    }
  }
  // if (isValue(source.nondata?.items)) {
  //   let nondata = source.nondata.items;
  //   let anno_count = 0, emph_count = 0;
  //   for (const ni in nondata) {
  //     let nondata_item = nondata[ni];
  //     if (nondata_item.type === "text") {
  //       let anno_id = "a" + anno_count.toString();
  //       id_mappings[anno_id] = { nondata: ni };
  //       if (isValue(nondata_item.name)) id_mappings[anno_id].name = nondata_item.name;
  //       extracted.push(["annotation", anno_id]);
  //       if (isValue(nondata_item.text)) {
  //         let l = 0;
  //         if (isValue(nondata_item.text, "Array")) {
  //           for (const text_item of nondata_item.text) l += text_item.text?.length || text_item.length
  //         } else if (isValue(nondata_item.text, "String")) l = nondata_item.text.length;
  //         extracted.push(["length", anno_id, l]);
  //       }
  //       if (nondata_item.role === "axis-assist") {
  //         extracted.push(["axis_assist", anno_id]);
  //         if (isValue(nondata_item.field)) extracted.push(["axis_assist", anno_id, nondata_item.field]);
  //       }
  //       if (nondata_item.visibility?.externalize) {
  //         extracted.push(["text_external", anno_id]);
  //         if (nondata_item.visibility?.externalize.numbering) extracted.push(["text_numbering", anno_id])
  //       }
  //       anno_count += 1;
  //     } else if (nondata_item.type === "mark") {
  //       let emph_id = "e" + emph_count.toString();
  //       id_mappings[emph_id] = { nondata: ni };
  //       if (isValue(nondata_item.name)) id_mappings[emph_id].name = nondata_item.name;
  //       extracted.push(["emphasis", emph_id]);
  //       emph_count += 1;
  //     }
  //   }
  // }

  // 8. interaction
  if (isValue(source.interaction?.zoompan)) extracted.push(["interaction", "zoom"])
  if (isValue(source.interaction?.context)) extracted.push(["interaction", "context"])
  if (source.interaction?.lock) {
    if (source.interaction.lock.zoompan) extracted.push(["locked", "zoom"]);
    if (source.interaction.lock.context) extracted.push(["locked", "context"]);
  }

  // 9. title
  if (isValue(source.title?.items)) {
    let title_count = 0;
    for (const hi in source.title.items) {
      let title_item = source.title.items[hi];
      let title_id = "h" + hi.toString();
      id_mappings[title_id] = { title: hi };
      extracted.push(["title", title_id]);
      if (title_count == 0) extracted.push(["first_title", title_id]);
      let length = title_item.text?.length;
      extracted.push(["length", title_id, length]);
      if (title_item.lock) {
        extracted.push(["locked", title_id]);
      }
      if (title_item.position_lock) {
        extracted.push(["position_locked", title_id]);
      }
      if (title_item.width) {
        extracted.push(["title_width_set", title_id]);
      }
      title_count += 1;
    }
  }

  // 10. multiples header
  if (isValue(source.layout?.split?.label)) {
    extracted.push(["header", "m0"])
    let text_id = "m0"
    id_mappings[text_id] = { type: "split.label" };
    if (isValue(source.layout.split.label.items, "Array")) {
      extracted.push(["text_lines", text_id, source.layout.split.label.items.length]);
      let check_br_wrapped = false;
      for (const elem of source.layout.split.label.items) {
        if (isValue(elem.width)) extracted.push(["text_wrapped", text_id]);
        if (isValue(elem.rotate)) extracted.push(["text_rotated", text_id]);
        if (isValue(elem.expression, "String")) {
          let simple_pattern1 = /\((datum\.)[a-zA-Z0-9\_\-]+\)/gi;
          let simple_pattern2 = /(datum\.)[a-zA-Z0-9\_\-]+/gi;
          if (elem.expression.match(simple_pattern1) !== null && elem.expression.match(simple_pattern2) !== null)
            extracted.push(["text_customed", text_id]);
        } else if (isValue(elem.custom) || isValue(elem.content)) extracted.push(["text_customed", text_id]);
        if (isValue(elem.text, "String") && elem.text.match(BR)) check_br_wrapped = true;
        if (isValue(elem.expression, "String") && elem.expression.match(BR)) check_br_wrapped = true;
        if (isValue(elem.custom)) {
          Object.keys(elem.custom).forEach((c) => {
            if (isValue(elem.custom[c], "String") && elem.custom[c].match(BR)) check_br_wrapped = true;
          });
        }
      }
      if (check_br_wrapped) {
        extracted.push(["text_br_wrapped", text_id]);
      }
    }
  }

  let feature_statements = extracted.map(d => {
    return d[0] + "(" + d.slice(1).join(",") + ").";
  })
  return { feature_statements, id_mappings, datatype }
}

function isValue(d, t) {
  if (t == undefined) return d !== undefined && d !== null;
  else return d?.constructor?.name === t;
}

function sign(d) {
  return (d == 0 ? 0 : (d < 0 ? -1 : 1))
}

export function getDatasetInfo(dataset) {
  let dataInstance = {};
  dataset.forEach(row => {
    Object.keys(row).forEach(key => {
      if (!dataInstance.hasOwnProperty(key)) dataInstance[key] = row[key];
    });
  });
  return Object.keys(dataInstance).map(fieldName => {
    let stat = getDataStat(dataset.map(d => d[fieldName]));
    return {
      field: fieldName,
      type: getVegaType(dataInstance[fieldName], fieldName),
      entropy: stat.entropy,
      cardinality: stat.cardinality,
      values: stat.unique,
      min: stat.min,
      max: stat.max
    }
  });
}

export function getDataRows(dataset) {
  if (dataset?.constructor.name === "Array") {
    return dataset.length;
  } else if (dataset?.constructor.name === "Object") {
    let k = Object.keys(dataset)[0]
    return dataset[k]?.length;
  } else {
    return null;
  }
}

export function getAspectRaitoType(ar) {
  if (ar > 1.1) return "landscape";
  else if (ar < 0.9) return "portrait";
  else return "square";
}

export function gtAbsRatio(a, b) {
  if (a < b || 1 / a > b) return true;
  else return false;
}